import React, { useState, useEffect } from 'react';
import { Badge } from '../Ui';
import EmrVisitDetail from './EmrVisitDetail';
import { ipApi } from '../../services/api';

/** Patient timeline — past Rx, vitals, history, documents */
export default function EmrMagicBox({ patient, visits, queuePreVitals, onLoadVisit, patientId, showPrivate, doctorName }) {
  const [tab, setTab] = useState('visits');
  const [selected, setSelected] = useState(null);
  const [docs, setDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const pid = patientId || patient?.patientId;

  const privateVisits = (visits || []).filter((v) => v.privateNotes);

  const lastVitals = visits.find((v) => v.vitals && Object.values(v.vitals).some(Boolean))?.vitals;
  const vitals = queuePreVitals && Object.values(queuePreVitals).some(Boolean) ? queuePreVitals : lastVitals;

  useEffect(() => {
    if (tab !== 'docs' || !pid) return;
    setDocsLoading(true);
    ipApi.getPatientDocuments(pid)
      .then((r) => setDocs(r.documents || []))
      .catch(() => setDocs([]))
      .finally(() => setDocsLoading(false));
  }, [tab, pid]);

  const tabs = ['visits', 'vitals', 'rx', 'history', ...(pid ? ['docs'] : []), ...(showPrivate ? ['private'] : [])];

  return (
    <>
      <div className="emr-sidebar card magic-box">
        <h3>Patient timeline</h3>
        <div className="magic-box-tabs">
          {tabs.map((t) => (
            <button key={t} type="button" className={`emr-chip sm${tab === t ? ' active' : ''}`} onClick={() => setTab(t)}>
              {t === 'visits' ? 'Past visits' : t === 'rx' ? 'Past Rx' : t === 'docs' ? 'Records' : t === 'private' ? 'Private' : t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>

        {tab === 'vitals' && (
          <div className="magic-box-panel">
            {!vitals ? <p className="hint">No vitals recorded.</p> : (
              <dl className="magic-kv">
                {vitals.bp && <><dt>BP</dt><dd>{vitals.bp}</dd></>}
                {vitals.pulse && <><dt>Pulse</dt><dd>{vitals.pulse}</dd></>}
                {vitals.temp && <><dt>Temp</dt><dd>{vitals.temp}°F</dd></>}
                {vitals.spo2 && <><dt>SpO₂</dt><dd>{vitals.spo2}%</dd></>}
                {vitals.weight && <><dt>Weight</dt><dd>{vitals.weight} kg</dd></>}
              </dl>
            )}
            {queuePreVitals && <p className="hint" style={{ fontSize: 11 }}>Includes pre-consult vitals from reception/staff.</p>}
          </div>
        )}

        {tab === 'history' && (
          <div className="magic-box-panel">
            <p className="hint"><strong>Allergies:</strong> {patient?.allergies || visits[0]?.history?.allergies || '—'}</p>
            <p className="hint"><strong>Chronic:</strong> {(patient?.chronicConditions || visits[0]?.history?.pmh || []).join?.(', ') || '—'}</p>
            <p className="hint"><strong>Current meds:</strong> {patient?.currentMeds || visits[0]?.history?.currentMeds || '—'}</p>
          </div>
        )}

        {tab === 'private' && (
          <div className="magic-box-panel">
            {!privateVisits.length ? <p className="hint">No private notes on record.</p> : privateVisits.map((v) => (
              <div key={v.recordId} className="magic-rx-card">
                <div className="visit-date">{v.createdAt ? new Date(v.createdAt).toLocaleDateString('en-IN') : '—'} · {v.doctorName || 'Doctor'}</div>
                <p className="hint" style={{ whiteSpace: 'pre-wrap' }}>{v.privateNotes}</p>
              </div>
            ))}
            <p className="hint" style={{ fontSize: 10 }}>Private notes are only visible to doctors.</p>
          </div>
        )}

        {tab === 'docs' && (
          <div className="magic-box-panel">
            {docsLoading && <p className="hint">Loading records…</p>}
            {!docsLoading && docs.length === 0 && <p className="hint">No uploaded medical records.</p>}
            {docs.map((d) => (
              <div key={d.documentId} className="magic-rx-card">
                <div className="visit-date">{d.fileName}</div>
                <p className="hint">{d.category} · {d.uploadedAt ? new Date(d.uploadedAt).toLocaleDateString('en-IN') : ''}</p>
              </div>
            ))}
          </div>
        )}

        {tab === 'rx' && (
          <div className="magic-box-panel">
            {!visits.length ? <p className="hint">No prescriptions yet.</p> : visits.slice(0, 5).map((v) => (
              <div key={v.recordId} className="magic-rx-card">
                <div className="visit-date">{v.createdAt ? new Date(v.createdAt).toLocaleDateString('en-IN') : '—'}</div>
                <p className="hint">{(v.rxLines || []).slice(0, 3).map((r) => r.drug).filter(Boolean).join(', ') || v.prescription || '—'}</p>
                <button type="button" className="btn btn-sm btn-secondary" onClick={() => onLoadVisit?.(v)}>Copy</button>
              </div>
            ))}
          </div>
        )}

        {tab === 'visits' && (
          <>
            {!visits?.length ? <p className="hint">No prior visits.</p> : (
              <ul className="visit-timeline">
                {visits.map((v) => (
                  <li key={v.recordId}>
                    <button type="button" className="visit-timeline-btn" onClick={() => setSelected(v)}>
                      <div className="visit-date">
                        {v.createdAt ? new Date(v.createdAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—'}
                      </div>
                      <Badge tone="info">{v.doctorName || 'Doctor'}</Badge>
                      <p className="visit-dx"><strong>{v.assessment?.primary || v.diagnosis || '—'}</strong></p>
                    </button>
                    {onLoadVisit && (
                      <button type="button" className="btn btn-sm btn-secondary visit-copy-btn" onClick={() => onLoadVisit(v)}>Copy</button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
      {selected && <EmrVisitDetail visit={selected} onClose={() => setSelected(null)} />}
    </>
  );
}
